<template>
  <div id="pm-analytics-modal-parent">
    <Page title="PM分析">
      <template v-slot:action>
        <ActionButton title="PM分析とは" color="primary" shape="round" width="150" @click="showTutorial" />
      </template>
      <template v-slot:content>
        <div class="uk-card uk-card-default uk-card-body" :class="{ 'main-hidden': activePath === 'sub' }">
          <div class="content-header">
            <ActionButton
              title="分析対象追加"
              color="primary"
              width="150"
              @click="onAddButtonClick"
              :uk-tooltip="`title: 分析可能人数は${participantsMin}人〜${participantsMax}人です。; pos: top-left;`"
            />
            <div class="right-contents">
              <ActionButton title="印刷" color="primary" shape="round" width="150" @click="onPrintButtonClick" />
              <ActionButton
                title="CSV出力"
                color="primary"
                shape="round"
                width="150"
                @click="onCsvButtonClick"
                :uk-tooltip="`title: CSVに退職者は含まれません。; pos: top-left;`"
              />
            </div>
          </div>
          <div class="content-main">
            <div class="selected-users">
              <UserSelector :users="selectedUsers" :readOnly="true" :selectable="true" @user-click="onUserSelect" />
            </div>
            <div class="chart">
              <PMAnalyticsChart :chartData="stats" />
            </div>
          </div>
        </div>
        <div class="uk-flex-top" uk-modal ref="selectionModal" container="#pm-analytics-modal-parent" :bg-close="false">
          <div class="uk-modal-dialog uk-margin-auto-vertical">
            <div class="uk-modal-body">
              <DepartmentUserSelector
                :key="departmentUserSelectorFormKey"
                :full="true"
                :analysisFlg="true"
                :includeRetired="true"
                :initialSelectedUsers="selectedUsers"
                :participantsMax="participantsMax"
                :participantsMin="participantsMin"
                @user-selection-change="onTargetUserSelectionChange"
                @on-selection-done-button-click="onSelectionDoneButtonClick"
                @close="onCloseButtonClick"
                v-on:keydown.esc="onCloseButtonClick"
              />
            </div>
          </div>
        </div>
      </template>
    </Page>
  </div>
</template>
<style lang="scss" scoped>
.action .action-button:first-child {
  margin-right: $spacing-medium;
}
.uk-card-body {
  .content-main {
    display: flex;
  }
  .content-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: $spacing-ex-small;
  }
}
.chart {
  flex: 1;
  position: relative;
}
.selected-users {
  width: 250px;
  margin-right: $spacing-medium;
  max-height: 500px;
  min-height: 300px;
  .user-selector-container {
    height: 100%;
  }
}
.uk-modal-dialog {
  width: unset;
  min-width: 960px;
}
.main-hidden {
  display: none;
}
.right-contents {
  margin: 0 0 0 auto;
  & > button:first-child {
    margin-right: 6px;
  }
}
</style>
<script lang="ts">
import { Options } from "vue-class-component";
import Page from "../molecules/Page.vue";
import ActionButton from "../atoms/ActionButton.vue";
import ActionMenu from "../molecules/ActionMenu.vue";
import UserSelector from "../molecules/UserSelector.vue";
import PMAnalyticsChart from "../atoms/PMAnalyticsChart.vue";
import DepartmentUserSelector from "../organisms/DepartmentUserSelector.vue";
import { SelectableProfile, GetPMAnalytics, GetProfile } from "../../types";
import { StatsItemEnum, SystemApi } from "../../autogen";
import { print } from "../../helpers/view-utils";
import ChartViewMixin from "../../helpers/mixins/chart-view";
import Store from "../../store";
import { InstructionLevel } from "../../types";

@Options({
  components: {
    Page,
    ActionButton,
    UserSelector,
    DepartmentUserSelector,
    ActionMenu,
    PMAnalyticsChart
  }
})
export default class PMAnalytics extends ChartViewMixin {
  selectedUsers: SelectableProfile[] = [];
  stats: (GetPMAnalytics & GetProfile)[] = [];
  targetStats = StatsItemEnum.PmAnalytics;
  participantsMin = 1;
  participantsMax = 100;
  activePath = "main";
  departmentUserSelectorFormKey = 0;
  loadingFunction = true;
  tutorialImagePath = require("@/assets/image/PMTutorial.png");
  async onSelectionDone() {
    this.stats = this.selectedUsers.map(selectedUser =>
      Object.assign(this.chartData![selectedUser.id].profile, this.chartData![selectedUser.id].stats["PMAnalytics"], {
        id: selectedUser.id
      })
    );
  }
  onPrintButtonClick() {
    print(StatsItemEnum.PmAnalytics, this.stats);
  }
  async onCsvButtonClick() {
    if (this.selectedUsers.length === 0) {
      Store.getInstance().setInstructionContent(
        InstructionLevel.Confirm,
        `全データが出力されますが、よろしいですか？\nデータが多いと時間がかかる場合があります。`,
        async () => {
          try {
            this.downLoadCsvFile(StatsItemEnum.PmAnalytics, ["all"]);
          } catch (err) {
            Store.getInstance().setInstructionContent(
              InstructionLevel.Alert,
              "エラーが発生しました。時間を置いて再度試すか、窓口までお問い合わせください。"
            );
          }
        }
      );
    } else {
      const Employees = this.selectedUsers.map(user => user.id);
      this.downLoadCsvFile(StatsItemEnum.PmAnalytics, Employees);
    }
  }
  private async downLoadCsvFile(statType: StatsItemEnum, Employees: string[]) {
    Store.getInstance().setIsLoading(true);

    const encoding = require("encoding-japanese");
    try {
      const responseStatic = await new SystemApi().printCsv({
        type: statType,
        peopleIds: Employees
      });

      const str_array = encoding.stringToCode(responseStatic.data);
      const sjis_array = encoding.convert(str_array, "SJIS", "UNICODE");
      const uint8_array = new Uint8Array(sjis_array);

      const url = window.URL.createObjectURL(new Blob([uint8_array], { type: "text/csv;charset=Shift_JIS" }));
      const anchor = document.createElement("a");
      document.body.appendChild(anchor);
      anchor.setAttribute("href", url);
      anchor.setAttribute("download", `PM分析_${new Date().toISOString()}.csv`);
      anchor.click();
      anchor.remove();
      document.body.onfocus = null;
    } catch (err) {
      let errMessage = "エラーが発生しました。時間を置いて再度試すか、窓口までお問い合わせください。";
      if (err.response && err.response.data) {
        errMessage = err.response.data.detail;
      }
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, errMessage);
    } finally {
      Store.getInstance().setIsLoading(false);
    }
  }
}
</script>
